import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/features/campaign'
import { CampaignFilter, ICampaignFilterConfig } from '@forwardflow/features'
import CampaignService from '@forwardflow/services/CampaignService'
import { useCampaignStore } from '@forwardflow/store'
import { useAdvanceTargetingCampaignModalStore } from '@forwardflow/store/modal/AdvanceTargetingCampaignModal'

import { message } from 'antd'
import { Modal, Overlay } from '@mantine/core'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { TextInput } from '@forwardflow/ui-v2'

export const NewCampaignModal = () => {
   const { title: leadSource } = useParams()
   const { data: activeLeadSourceData, mutate: mutateActiveLeadSource } =
      useGetActiveLeadSource(leadSource as string)
   const { mutate: mutateActiveLeadSourceDetails } =
      useGetActiveLeadSourceDetails(leadSource as string)
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()

   const {
      campaignConfiguration,
      resetCampaignConfiguration,
      setCampaignConfiguration,
   } = useCampaignStore()

   const {
      isAdvanceTargetingCampaignModalOpen,
      closeAdvanceTargetingCampaignModal,
   } = useAdvanceTargetingCampaignModalStore()
   const [isLoading, setIsLoading] = useState(false)
   const [nameError, setNameError] = useState<string>('')

   const isEditMode = false

   const handleLaunchCampaign = async () => {
      const currentConfig = useCampaignStore.getState().campaignConfiguration

      // Validate campaign name
      if (!currentConfig?.campaign_name?.trim()) {
         setNameError('Campaign name is required')
         return
      }
      setNameError('')

      setIsLoading(true)
      try {
         let responseActiveLeadSource: any
         let responseUpdatedLeadSource: any
         if (isEditMode && activeLeadSourceData?.id) {
            responseActiveLeadSource =
               await CampaignService.editCustomActiveLeadSource(
                  activeLeadSourceData?.id,
                  currentConfig as any
               )
            responseUpdatedLeadSource =
               await CampaignService.updateCampaignName(
                  activeLeadSourceData?.id,
                  currentConfig?.campaign_name
               )
         } else {
            responseActiveLeadSource =
               await CampaignService.createCustomActiveLeadSource(
                  currentConfig?.campaign_name as string,
                  currentConfig?.campaign_filters as any,
                  currentConfig?.campaign_type as unknown as
                     | 'new-lead'
                     | 'advanced',
                  {
                     bufferTime:
                        currentConfig?.first_to_contact?.buffer_time ||
                        0,
                  },
                  true,
                  // Activity campaigns carry their events + pond triggers so they're persisted at
                  // creation (null for non-activity types — the backend normalizes/ignores it).
                  (currentConfig as any)?.activity_campaign_filter_criteria ??
                     null,
                  (currentConfig as any)?.lead_type_filter
               )
         }
         if (responseUpdatedLeadSource) {
            message.success('Campaign updated successfully')

            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.set(
               'campaignName',
               responseUpdatedLeadSource?.campaignName
            )
            navigate(
               `${window.location.pathname}?${newSearchParams.toString()}`,
               { replace: true }
            )
         }
         if (responseActiveLeadSource && responseActiveLeadSource.id) {
            if (isEditMode) {
               message.success('Campaign updated successfully')
            } else {
               navigate(
                  `/campaign/${encodeURIComponent(
                     responseActiveLeadSource.id
                  )}/phone-number?campaignName=${
                     currentConfig?.campaign_name
                  }`
               )
            }
         }
      } catch (e) {
         message.error(
            isEditMode
               ? 'Failed to update campaign'
               : 'Failed to create campaign'
         )
      } finally {
         closeAdvanceTargetingCampaignModal()
         setIsLoading(false)
         mutateActiveLeadSource()
         mutateActiveLeadSourceDetails()
         resetCampaignConfiguration()
      }
   }

   return (
      <Modal
         centered
         title={
            <div>
               <p className="text-xl font-medium">New Campaign</p>
               <p className="text-sm text-white/50 font-normal mt-1">Set up your campaign filters and targeting preferences</p>
            </div>
         }
         size="xl"
         radius="lg"
         withCloseButton
         classNames={{
            body: 'px-6 pb-6 pt-2',
            header: 'px-6 pt-6 pb-4',
            title: '',
         }}
         opened={isAdvanceTargetingCampaignModalOpen}
         onClose={closeAdvanceTargetingCampaignModal}
      >
         {isLoading && (
            <Overlay
               blur={3}
               center
               fixed={false}
               className="z-50"
               styles={{
                  root: {
                     position: 'absolute',
                  },
               }}
            >
               <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                  <p className="text-white font-medium">
                     {isEditMode
                        ? 'Updating campaign...'
                        : 'Creating campaign...'}
                  </p>
               </div>
            </Overlay>
         )}
         <div className="max-w-[850px] mx-auto space-y-6">
            <TextInput
               label="Campaign Name"
               name="campaign_name"
               placeholder="Enter campaign name"
               value={campaignConfiguration?.campaign_name}
               onChange={(e) => {
                  setCampaignConfiguration({
                     ...campaignConfiguration,
                     campaign_name: e.target.value,
                  })
                  if (nameError) {
                     setNameError('')
                  }
               }}
               error={nameError}
            />
            <CampaignFilter
               initialConfiguration={{
                  campaign_type: campaignConfiguration?.campaign_type,
                  campaign_filters: campaignConfiguration?.campaign_filters || {
                     eligible_criteria: [],
                  },
                  first_to_contact: campaignConfiguration?.first_to_contact || {
                     buffer_time: 0,
                  },
                  // Keep activity events/triggers across re-syncs so a re-render mid-edit
                  // doesn't wipe the user's selection before the campaign is created.
                  activity_campaign_filter_criteria: (
                     campaignConfiguration as any
                  )?.activity_campaign_filter_criteria,
               }}
               onSave={(config: ICampaignFilterConfig) => {
                  setCampaignConfiguration(config)
               }}
               onNextStep={handleLaunchCampaign}
               submitLabel="Create Campaign"
            />
         </div>
      </Modal>
   )
}
